import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ordersService } from '@/services';
import { ApiError } from '@/services/api';
import type { Order, OrderStatus } from '@/types/order';
import { formatPrice } from '@/utils/format';
import { Badge } from '@/components/ui';

function statusLabel(status: OrderStatus, t: (key: string) => string): string {
  const key = `orders.status_${status}`;
  const translated = t(key);
  return translated === key ? status : translated;
}

function statusVariant(status: OrderStatus): 'success' | 'warning' | 'danger' | 'neutral' | 'brand' {
  if (status === 'PAGADA') return 'success';
  if (status === 'PAGO_PENDIENTE') return 'warning';
  if (status === 'CANCELADA') return 'danger';
  return 'neutral';
}

export function OrdersListPage(): JSX.Element {
  const { t } = useTranslation('common');
  const { data: orders, isLoading, error } = useQuery({
    queryKey: ['orders', 'my'],
    queryFn: () => ordersService.getMyOrders(),
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center px-4">
        <p className="font-sans text-brand-700">{t('common.loading')}</p>
      </div>
    );
  }

  if (error) {
    const message = error instanceof ApiError ? error.message : t('orders.loadError');
    return (
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="rounded-md bg-red-50 p-4 text-red-800">{message}</div>
      </div>
    );
  }

  const list = orders ?? [];

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:px-8">
      <h1 className="mb-6 font-heading text-heading-xl text-brand-950">{t('orders.title')}</h1>

      {list.length === 0 ? (
        <p className="font-sans text-brand-700">{t('orders.empty')}</p>
      ) : (
        <ul className="space-y-3">
          {list.map((order: Order) => (
            <li key={order.id}>
              <Link
                to={`/orders/${order.id}`}
                className="block rounded-lg border border-brand-700/20 bg-surface p-4 transition-colors hover:border-brand-400"
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-heading text-heading-sm text-brand-950">
                      {t('orders.orderShort', { id: order.id.slice(0, 8) })}
                    </p>
                    <p className="font-sans text-sm text-brand-700">
                      {new Date(order.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 sm:justify-end">
                    <Badge variant={statusVariant(order.status)}>{statusLabel(order.status, t)}</Badge>
                    <span className="font-heading text-heading-md text-brand-950">
                      {formatPrice(order.totalAmountCop, 'COP')}
                    </span>
                    <span className="font-sans text-sm text-brand-600">{t('orders.viewDetail')} →</span>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
